// Ob'ektlar (venue) — xsib-core `venue` moduli bilan haqiqiy kontrakt.
// Manba: `venue/internal/VenueAdminController.java` (+ `venue/api/*.java`).
//
// ⚠️ Venue TENANT EMAS: bitta tenantda o'nlab Venue bo'lishi mumkin —
// mehmonxona tarmog'ining har filiali alohida Venue (`Venue.java` javadoc).
//
// ⚠️ Holat o'zgarishi (`/state`) — `OperationResult` EMAS, to'g'ridan-to'g'ri
// yangilangan obyektni qaytaradi. Sabab: bu yerda CoA/RADIUS operativ holati
// yo'q (ISP tomonidagi bloklash/tezlik bilan solishtiring) — faqat baza
// yozuvi, shuning uchun muvaffaqiyat oddiy HTTP javob bilan bildiriladi va
// xato bo'lsa odatdagi HTTP xato sifatida keladi (`translateError`).
//
// ⚠️ `/state` va `/zones/{id}/state` — POST, lekin tana YO'Q: qiymatlar
// QUERY PARAMETR (backend `@RequestParam`), JSON tana emas.

import { z } from 'zod'
import { api } from '@/shared/api/client'
import { instantSchema, uuidSchema } from '@/shared/api/schemas'
import {
  fetchAllRows,
  listParams,
  serverPageSchema,
  toPageResult,
  type SortFieldMap,
} from '@/shared/api/serverPage'
import type { PageResult } from '@/shared/ui/table/DataTable'
import type { TableState } from '@/shared/ui/table/tableState'

export const VENUE_KINDS =['HOTEL', 'CAFE', 'COWORKING', 'TRANSPORT', 'OTHER'] as const
export type VenueKind = (typeof VENUE_KINDS)[number]

export const VENUE_STATES = ['ACTIVE', 'SUSPENDED', 'CLOSED'] as const
export type VenueState = (typeof VENUE_STATES)[number]

/** Mehmonning internetini kim to'laydi — `subscriber.FundedBy` ning qism to'plami */
export const VENUE_FUNDINGS = ['VENUE', 'ADVERTISER'] as const
export type VenueFunding = (typeof VENUE_FUNDINGS)[number]

export const ZONE_STATES = ['ACTIVE', 'SUSPENDED'] as const
export type ZoneState = (typeof ZONE_STATES)[number]

/** Mehmon qanday identifikatsiya qilingani — `IdentificationMethod.java` */
export const IDENTIFICATION_METHODS = [
  'SMS_OTP',
  'ROOM_PMS',
  'VOUCHER',
  'SOCIAL',
  'STAFF_MANUAL',
  'DEVICE_MEMO',
] as const
export type IdentificationMethod = (typeof IDENTIFICATION_METHODS)[number]

/**
 * Mehmon portali HOZIRCHA ekran chizgan usullar.
 *
 * ⚠️ NEGA BU RO'YXAT ADMIN PANELIDA KERAK. Backend oltita usulning
 * hammasini qabul qiladi, lekin portal ikkitasigagina ekran chizgan
 * (`frontend/portal/.../api.ts` → `SUPPORTED_METHODS`). Ya'ni operator
 * zonaga faqat "Xona raqami (PMS)" ni qo'ysa, panel buni JIMGINA saqlaydi
 * va nosozlik faqat mehmon Wi-Fi'ga ulanganda — "kirish usuli yo'q" degan
 * ekran bilan — bilinadi. Buni panelda AYTISH kerak, mehmonda emas.
 *
 * ⚠️ Bu ro'yxat portalnikidan NUSXA — uch panel mustaqil ilova.
 * Portalda yangi ekran qurilsa, shu yerga ham qo'shing, aks holda panel
 * ishlaydigan usulni "ishlamaydi" deb ko'rsatib turadi.
 */
export const PORTAL_SUPPORTED_METHODS: readonly IdentificationMethod[] = ['SMS_OTP', 'VOUCHER']

/** Zona mehmonni umuman ichkariga kirita oladimi — portal ekrani bormi */
export function hasPortalScreen(methods: readonly IdentificationMethod[]): boolean {
  return methods.some((m) => PORTAL_SUPPORTED_METHODS.includes(m))
}

/**
 * Har usul ENG YAXShI holatda qanday huquqiy kuch bera oladi.
 *
 * ⚠️ NEGA "ENG YAXShI". `VOUCHER` ning kuchi sotuvga bog'liq: shaxs
 * tasdiqlangan holda sotilgan karta {@code DELEGATED}, anonim sotilgani
 * {@code INSUFFICIENT} (`IdentificationService.resolveStrength`).
 * Ya'ni bitta usul ikki xil natija berishi mumkin va biz eng
 * ILIQ ehtimolni olamiz — shunda ogohlantirish faqat HECH QACHON
 * ishlamaydigan holatda chiqadi va "yolg'on xavotir" bo'lmaydi.
 *
 * `DEVICE_MEMO` ro'yxatda YO'Q: uning kuchi oldingi tashrifdan meros
 * bo'lib o'tadi, ya'ni oldindan aytib bo'lmaydi.
 *
 * ⚠️ Manbasi — `IdentificationMethod.java`. O'sha yerda o'zgarsa, bu
 * yerda ham o'zgartiring.
 */
const METHOD_BEST_STRENGTH: Partial<Record<IdentificationMethod, IdentificationStrength>> = {
  SMS_OTP: 'SUFFICIENT',
  ROOM_PMS: 'DELEGATED',
  VOUCHER: 'DELEGATED',
  SOCIAL: 'INSUFFICIENT',
  STAFF_MANUAL: 'DELEGATED',
}

const STRENGTH_ORDER: Record<IdentificationStrength, number> = {
  INSUFFICIENT: 0,
  DELEGATED: 1,
  SUFFICIENT: 2,
}

/**
 * Usul shu zonada UMUMAN o'ta oladimi.
 *
 * ⚠️ Bu tekshiruv sinovda topilgan holat uchun: zonada `VOUCHER` ruxsat
 * etilgan, `minStrength` esa `SUFFICIENT` edi. Panel buni jimgina
 * saqlagan, mehmon esa HAQIQIY kodni kiritib "bu yerda shaxsni
 * tasdiqlash uchun yetarli emas" javobini olgan. Ya'ni usul ko'rinadi,
 * ishlaydigandek tuyuladi, lekin hech qachon o'tmaydi.
 */
export function methodBlockedByStrength(
  method: IdentificationMethod,
  minStrength: IdentificationStrength | null,
): boolean {
  const best = METHOD_BEST_STRENGTH[method]
  if (!best || !minStrength) return false
  return STRENGTH_ORDER[best] < STRENGTH_ORDER[minStrength]
}

/**
 * Vaucher shu zonada FAQAT SOTILGAN bo'lsa o'tadimi.
 *
 * ⚠️ Kartaning huquqiy kuchi `sold_at` dan kelib chiqadi
 * (`GuestVoucherServiceImpl.peek`): agent sotgan karta `DELEGATED`,
 * sotilmagani `INSUFFICIENT`. Ya'ni `DELEGATED` talab qiladigan zonada
 * chop etilgan, lekin hali sotilmagan karta RAD ETILADI.
 *
 * Bu qoida to'g'ri (karta pulni tasdiqlaydi, shaxsni emas — sotuv esa
 * xaridorni bog'laydi), lekin uni operator BILISHI kerak: aks holda
 * u kartalarni bepul tarqatadi va nima uchun ishlamayotganini
 * tushunmaydi.
 */
export function voucherNeedsSale(
  methods: readonly IdentificationMethod[],
  minStrength: IdentificationStrength | null,
): boolean {
  return methods.includes('VOUCHER') && minStrength === 'DELEGATED'
}

/** Identifikatsiyaning huquqiy kuchi — №137 uchun yetarlimi */
export const IDENTIFICATION_STRENGTHS = ['SUFFICIENT', 'DELEGATED', 'INSUFFICIENT'] as const
export type IdentificationStrength = (typeof IDENTIFICATION_STRENGTHS)[number]

// --- Venue --------------------------------------------------------------

const venueSchema = z.object({
  id: uuidSchema,
  tenantId: uuidSchema,
  code: z.string(),
  name: z.string(),
  kind: z.enum(VENUE_KINDS),
  timezone: z.string(),
  contactName: z.string().nullish(),
  contactPhone: z.string().nullish(),
  funding: z.enum(VENUE_FUNDINGS),
  piiRetentionDays: z.number(),
  journalRetentionDays: z.number(),
  state: z.enum(VENUE_STATES),
  createdAt: instantSchema.nullish(),
  updatedAt: instantSchema.nullish(),
})

export interface Venue {
  id: string
  code: string
  name: string
  kind: VenueKind
  timezone: string
  contactName: string | null
  contactPhone: string | null
  funding: VenueFunding
  piiRetentionDays: number
  journalRetentionDays: number
  state: VenueState
  createdAt: string | null
  updatedAt: string | null
}

function toVenue(raw: z.infer<typeof venueSchema>): Venue {
  return {
    id: raw.id,
    code: raw.code,
    name: raw.name,
    kind: raw.kind,
    timezone: raw.timezone,
    contactName: raw.contactName ?? null,
    contactPhone: raw.contactPhone ?? null,
    funding: raw.funding,
    piiRetentionDays: raw.piiRetentionDays,
    journalRetentionDays: raw.journalRetentionDays,
    state: raw.state,
    createdAt: raw.createdAt ?? null,
    updatedAt: raw.updatedAt ?? null,
  }
}

export interface VenueDraft {
  code: string
  name: string
  kind: VenueKind
  timezone?: string | null
  contactName?: string | null
  contactPhone?: string | null
  funding?: VenueFunding | null
  /** `null` = server sukut qiymati (365 kun) ishlatadi */
  piiRetentionDays?: number | null
  /** `null` = server sukut qiymati (1095 kun) ishlatadi */
  journalRetentionDays?: number | null
}

/** URL filtri kaliti — jadval ustuni va sarlavhadagi tanlagich bir xil nom ishlatadi */
export const VENUE_STATE_FILTER = 'state'

/**
 * «Barcha holatlar» — filtrning ATAYLAB tanlanadigan qiymati, server
 * holati EMAS. Shuning uchun u `VENUE_STATES` ga qo'shilmaydi: qo'shilsa
 * so'rovga `state=ALL` bo'lib ketar va server 400 qaytarardi.
 */
export const VENUE_STATE_ALL = 'ALL'

/**
 * Sukut ko'rinish — FAQAT AMALDAGI ob'ektlar (egasi qarori 2026-08-20).
 *
 * ⚠️ Ilgari sukut «barcha holatlar» edi. Ob'ekt hech qachon
 * O'CHIRILMAYDI (mehmon jurnali qonun bo'yicha saqlanadi), ya'ni
 * yopilganlar yillar davomida to'planadi va ro'yxatning katta qismini
 * egallab qo'yadi.
 *
 * ⚠️ To'xtatilgan ob'ekt ham yashirinadi — u ham «amaldagi» emas. Uni
 * QAYTA YOQISH yo'li yo'qolmaydi: holat filtrida «To'xtatilgan» tanlovi
 * turadi va so'rov serverga ROSTDAN boradi.
 */
export const VENUE_DEFAULT_STATE: VenueState = 'ACTIVE'

export function isVenueState(value: string): value is VenueState {
  return (VENUE_STATES as readonly string[]).includes(value)
}

/**
 * Jadval holatidan `state` so'rov parametri.
 *
 * ⚠️ ATAYLAB EKSPORT QILINGAN va `fetchVenuePage` dan AJRATILGAN: sukut
 * qiymatni CHAQIRIB o'lchash kerak. Manba matnidagi naqsh
 * («`VENUE_DEFAULT_STATE` ishlatiladimi») bu farqni ko'rmasdi — nom
 * joyida turib, sukut ikkinchi sahifada tushib qolishi mumkin
 * (`tests/closed-default.mjs`, `closed-filter.mjs` naqshi).
 *
 * ⚠️ Noma'lum qiymat (eski havola, qo'lda tahrirlangan URL) SUKUTGA
 * qaytadi, «barchasi» ga EMAS: aks holda buzuq havola yopilganlarni
 * jimgina qaytarib olib kelardi.
 */
export function venueStateParam(state: TableState): Record<string, string> {
  const selected = state.filters[VENUE_STATE_FILTER]?.[0]
  if (selected === VENUE_STATE_ALL) return {}
  if (selected !== undefined && isVenueState(selected)) return { state: selected }
  return { state: VENUE_DEFAULT_STATE }
}

/**
 * Ustun kaliti → `GET /api/venues` ning `sortField` enumi.
 *
 * ⚠️ ESKI SUKUT TARTIB (`name`) YO'Q — endi `CREATED_AT DESC`. Alifbo
 * tartibi kerak bo'lsa «Nomi» ustuni bo'yicha saralanadi.
 */
const VENUE_SORT_FIELDS: SortFieldMap = {
  code: 'CODE',
  name: 'NAME',
  kind: 'KIND',
  state: 'STATE',
  createdAt: 'CREATED_AT',
}

async function loadVenuePage(
  params: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<{ rows: Venue[]; total: number }> {
  const response = await api.get<unknown>('/api/venues', {
    params,
    ...(signal ? { signal } : {}),
  })
  const parsed = serverPageSchema(venueSchema).parse(response.data)
  return { rows: parsed.rows.map(toVenue), total: parsed.total }
}

/**
 * Bir SAHIFA ob'ekt — qidiruv, saralash, holat filtri va sahifalash
 * SERVERDA.
 *
 * ⚠️ Holat filtri har doim server tomonda edi va shunday qoladi: ob'ekt
 * hech qachon O'CHIRILMAYDI (mehmon jurnali qonun bo'yicha saqlanadi),
 * ya'ni yopilganlar yillar davomida to'planadi. Sukut qiymat
 * `venueStateParam` da.
 */
export async function fetchVenuePage(
  state: TableState,
  signal: AbortSignal,
): Promise<PageResult<Venue>> {
  const page = await loadVenuePage(
    { ...listParams(state, { sortFields: VENUE_SORT_FIELDS }), ...venueStateParam(state) },
    signal,
  )
  return toPageResult(page, page.rows)
}

/**
 * BUTUN ro'yxat — tanlagichlar uchun (kampaniya, tiket, lid, NAS zonasi),
 * jadval uchun EMAS.
 *
 * ⚠️ `state` MAJBURIY va sukut qiymati YO'Q. Sukut qiymat bilan bu
 * funksiyani `queryFn: fetchVenues` deb to'g'ridan-to'g'ri berish mumkin
 * bo'lardi — o'shanda TanStack unga so'rov KONTEKSTINI uzatadi va u
 * jimgina holat sifatida qabul qilinardi. Majburiy parametr bu xatoni
 * kompilyatsiyada ushlaydi.
 *
 * @param state `null` = barcha holatlar
 */
export async function fetchVenues(state: VenueState | null): Promise<Venue[]> {
  return fetchAllRows((page, size) =>
    loadVenuePage({ page, size, ...(state ? { state } : {}) }),
  )
}

export async function fetchVenue(id: string): Promise<Venue> {
  const response = await api.get<unknown>(`/api/venues/${id}`)
  return toVenue(venueSchema.parse(response.data))
}

export async function createVenue(draft: VenueDraft): Promise<Venue> {
  const response = await api.post<unknown>('/api/venues', draft)
  return toVenue(venueSchema.parse(response.data))
}

export async function updateVenue(id: string, draft: VenueDraft): Promise<Venue> {
  const response = await api.put<unknown>(`/api/venues/${id}`, draft)
  return toVenue(venueSchema.parse(response.data))
}

/** Sabab ixtiyoriy, lekin audit jurnaliga tushadi */
export async function setVenueState(id: string, state: VenueState, reason?: string): Promise<Venue> {
  const response = await api.post<unknown>(`/api/venues/${id}/state`, undefined, {
    params: { state, ...(reason ? { reason } : {}) },
  })
  return toVenue(venueSchema.parse(response.data))
}

// --- Zona -----------------------------------------------------------------

const venueZoneSchema = z.object({
  id: uuidSchema,
  tenantId: uuidSchema,
  venueId: uuidSchema,
  code: z.string(),
  name: z.string(),
  ssid: z.string().nullish(),
  nasIdentifier: z.string().nullish(),
  nasIp: z.string().nullish(),
  policyId: uuidSchema.nullish(),
  sessionQuotaBytes: z.number().nullish(),
  sessionSeconds: z.number().nullish(),
  grantValidMinutes: z.number(),
  allowedMethods: z.array(z.enum(IDENTIFICATION_METHODS)),
  minStrength: z.enum(IDENTIFICATION_STRENGTHS),
  deviceGraceMinutes: z.number(),
  /** `null` = ob'ektning `funding` qiymatidan meros olinadi */
  funding: z.enum(VENUE_FUNDINGS).nullish(),
  state: z.enum(ZONE_STATES),
  createdAt: instantSchema.nullish(),
  updatedAt: instantSchema.nullish(),
})

export interface VenueZone {
  id: string
  venueId: string
  code: string
  name: string
  ssid: string | null
  nasIdentifier: string | null
  nasIp: string | null
  policyId: string | null
  sessionQuotaBytes: number | null
  sessionSeconds: number | null
  grantValidMinutes: number
  allowedMethods: readonly IdentificationMethod[]
  minStrength: IdentificationStrength
  deviceGraceMinutes: number
  funding: VenueFunding | null
  state: ZoneState
}

function toZone(raw: z.infer<typeof venueZoneSchema>): VenueZone {
  return {
    id: raw.id,
    venueId: raw.venueId,
    code: raw.code,
    name: raw.name,
    ssid: raw.ssid ?? null,
    nasIdentifier: raw.nasIdentifier ?? null,
    nasIp: raw.nasIp ?? null,
    policyId: raw.policyId ?? null,
    sessionQuotaBytes: raw.sessionQuotaBytes ?? null,
    sessionSeconds: raw.sessionSeconds ?? null,
    grantValidMinutes: raw.grantValidMinutes,
    allowedMethods: raw.allowedMethods,
    minStrength: raw.minStrength,
    deviceGraceMinutes: raw.deviceGraceMinutes,
    funding: raw.funding ?? null,
    state: raw.state,
  }
}

export interface VenueZoneDraft {
  code: string
  name: string
  ssid?: string | null
  nasIdentifier?: string | null
  nasIp?: string | null
  policyId?: string | null
  sessionQuotaBytes?: number | null
  sessionSeconds?: number | null
  /** `null` = server sukut qiymati (720 daq / 12 soat) */
  grantValidMinutes?: number | null
  allowedMethods: readonly IdentificationMethod[]
  /** `null` = server sukut qiymati (`DELEGATED`) */
  minStrength?: IdentificationStrength | null
  deviceGraceMinutes?: number | null
  funding?: VenueFunding | null
}

export async function fetchZones(venueId: string): Promise<VenueZone[]> {
  const response = await api.get<unknown>(`/api/venues/${venueId}/zones`)
  return z.array(venueZoneSchema).parse(response.data).map(toZone)
}

export async function createZone(venueId: string, draft: VenueZoneDraft): Promise<VenueZone> {
  const response = await api.post<unknown>(`/api/venues/${venueId}/zones`, draft)
  return toZone(venueZoneSchema.parse(response.data))
}

export async function updateZone(zoneId: string, draft: VenueZoneDraft): Promise<VenueZone> {
  const response = await api.put<unknown>(`/api/venues/zones/${zoneId}`, draft)
  return toZone(venueZoneSchema.parse(response.data))
}

export async function setZoneState(zoneId: string, state: ZoneState): Promise<VenueZone> {
  const response = await api.post<unknown>(`/api/venues/zones/${zoneId}/state`, undefined, {
    params: { state },
  })
  return toZone(venueZoneSchema.parse(response.data))
}

// --- Xodim mehmonga qo'lda kirish beradi (qabulxona yo'li) -----------------
//
// Manba: `venueadmin/resource/VenueAdminResource#grantGuestAccess`,
// `guestaccess/dto/StaffGuestAccessRequest`, `GuestAccessResult`.
//
// ⚠️ SO'ROVDA ATAYLAB KAM MAYDON BOR. Siyosat, kvota, muddat va
// moliyalashtirish YUBORILMAYDI — hammasi zona sozlamasidan serverda
// olinadi. Bu `venue.guest.grant` huquqining butun mavjudlik sababi:
// ob'ekt menejeri "qaysi zona, qaysi qurilma, qaysi mehmon" deydi,
// undan ortig'ini emas.
//
// ⚠️ `staffUserId` HECH QACHON yuborilmaydi — server uni tokendan
// oladi. Mijozdan qabul qilish jurnalni soxtalashtirish imkonini
// berardi.

export interface StaffGuestAccessDraft {
  zoneId: string
  /** Normallashtirish SERVERDA; noto'g'ri shakl 400 beradi */
  mac: string
  fullName?: string | null
  documentRef?: string | null
  locale?: string | null
}

const guestAccessResultSchema = z.object({
  session: z.object({ id: uuidSchema }).nullish(),
  /** ⚠️ Mehmon TERADIGAN nom — `login@realm` shaklida */
  login: z.string().nullish(),
  /** X-SIB ichidagi toza login — qidiruv va abonent kartochkasi uchun */
  subscriberLogin: z.string().nullish(),
  /** ⚠️ BIR MARTALIK — boshqa hech qayerdan olib bo'lmaydi */
  password: z.string().nullish(),
  expiresAt: instantSchema.nullish(),
  quotaBytes: z.number().nullish(),
  quotaSeconds: z.number().nullish(),
})

export interface GuestAccessResult {
  sessionId: string | null
  login: string | null
  subscriberLogin: string | null
  password: string | null
  expiresAt: string | null
  quotaBytes: number | null
  quotaSeconds: number | null
  /**
   * Kirish HOZIR ochilganmi.
   *
   * ⚠️ `false` — XATO EMAS. Reklama bilan moliyalashtiriladigan zonada
   * birinchi qadam faqat JURNAL yozadi; kirish mehmon rolikni
   * ko'rgandan keyin ochiladi. Shu sababdan login va parol `null`
   * bo'ladi va panel buni "xato" deb ko'rsatmasligi kerak.
   */
  hasAccess: boolean
}

export async function grantGuestAccess(draft: StaffGuestAccessDraft): Promise<GuestAccessResult> {
  const response = await api.post<unknown>('/api/venues/guest-access', draft)
  const raw = guestAccessResultSchema.parse(response.data)
  return {
    sessionId: raw.session?.id ?? null,
    login: raw.login ?? null,
    subscriberLogin: raw.subscriberLogin ?? null,
    password: raw.password ?? null,
    expiresAt: raw.expiresAt ?? null,
    quotaBytes: raw.quotaBytes ?? null,
    quotaSeconds: raw.quotaSeconds ?? null,
    hasAccess: Boolean(raw.login && raw.password),
  }
}
